import { useState } from "react";
import { Star } from "lucide-react";

const StarRating = ({ value = 0, onChange, disabled = false, size = 28 }) => {
  const [hoverValue, setHoverValue] = useState(0);
  const totalStars = 5;

  const handleClick = (starIndex, e) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const isHalf = clickX < rect.width / 2;
    const rating = isHalf ? starIndex - 0.5 : starIndex;
    onChange?.(rating);
  };

  const handleMouseMove = (starIndex, e) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const hoverX = e.clientX - rect.left;
    const isHalf = hoverX < rect.width / 2;
    setHoverValue(isHalf ? starIndex - 0.5 : starIndex);
  };

  const handleMouseLeave = () => {
    if (!disabled) setHoverValue(0);
  };

  const activeValue = hoverValue || value;

  const getStarFill = (starIndex) => {
    if (activeValue >= starIndex) return "full";
    if (activeValue >= starIndex - 0.5) return "half";
    return "empty";
  };

  return (
    <div
      className="inline-flex items-center gap-1"
      onMouseLeave={handleMouseLeave}
    >
      {Array.from({ length: totalStars }, (_, i) => {
        const starIndex = i + 1;
        const fill = getStarFill(starIndex);

        return (
          <button
            key={starIndex}
            type="button"
            disabled={disabled}
            onClick={(e) => handleClick(starIndex, e)}
            onMouseMove={(e) => handleMouseMove(starIndex, e)}
            className={`relative transition-transform duration-200 ${
              disabled ? "cursor-default" : "cursor-pointer hover:scale-110"
            } ${fill !== "empty" ? "scale-105" : ""}`}
            style={{ width: size, height: size }}
            aria-label={`Rate ${starIndex} star${starIndex > 1 ? "s" : ""}`}
          >
            {/* Empty star (background) */}
            <Star
              size={size}
              className="absolute inset-0 text-gray-300"
              strokeWidth={1.5}
            />

            {/* Filled star — full or half via clip */}
            {fill !== "empty" && (
              <Star
                size={size}
                className="absolute inset-0 text-secondary-500 fill-secondary-500 transition-all duration-150"
                strokeWidth={1.5}
                style={
                  fill === "half"
                    ? { clipPath: "inset(0 50% 0 0)" }
                    : undefined
                }
              />
            )}
          </button>
        );
      })}

      {/* Numeric display */}
      {value > 0 && (
        <span className="ml-2 text-sm font-semibold text-gray-700 min-w-[2rem]">
          {value.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export default StarRating;
